/**
 * @class
 * @classdesc License input contains events for inserting license numbers to two fields (letters and numbers).
 *            For example: when three letters is written to letter field, automatically jump and select
 *            all contents in numbers field.
 *
 * @author Eero Ahola <eero.ahola@crasman.fi>
 * @property {object|string} inputs - Contains firstInput (letters) and lastInput (numbers).
 *                                    If string, constructor executes querySelector
 *
 * @example // HTML
 * //<div class="license-input-wrapper" id="test-license-input">
 * // <input type="text" name="license_letters" placeholder="ABC">
 * // <input type="text" name="license_numbers" placeholder="123">
 * //</div>
 *
 * @example
 * import LicenseInput from './license-input';
 * const licenseInputs = new LicenseInput(document.getElementById('input-fields-wrapper'));
 *
 * @example
 * import LicenseInput from './license-input';
 * const licenseInputs = new LicenseInput('#input-fields-wrapper');
 *
 * @example
 * // You can also store inputs for example to window -object for later use.
 * import LicenseInput from './license-input';
 * const inputWrapper = document.getElementsByClassName('license-input-wrapper');
 * for(let i = 0; i < inputWrapper.length; i++) {
 *  window.licenseInputWrappers[inputWrapper[i].id] = new LicenseInput(inputWrapper[i]);
 * }
 *
 * // You can use license input in window -property
 * const currentLicense = window.licenseInputWrappers['test-license-input'].getLicense();
 */
class LicenseInput
{
  /**
   * Constructor creates events to inputsWrapper inputs and returns its functions
   *
   * @param {object|string} inputsWrapper - Object got from i.e. getElementById or just string
   */
  constructor(inputsWrapper) {
    if(typeof inputsWrapper !== 'string' && typeof inputsWrapper !== 'object') {
      console.error('inputsWrapper is not and object or a string!');
      return;
    }

    // Get inputsWrapper via querySelector
    if(typeof inputsWrapper === 'string') {
      inputsWrapper = document.querySelector(inputsWrapper);

      if(!inputsWrapper) {
        console.error('Could not find inputsWrapper!');
        return;
      }
    }

    // Checkt that ID exists in container element
    if(typeof inputsWrapper.id === 'undefined' || inputsWrapper.id === '' || inputsWrapper.id === null) {
      console.error('License inputs wrapper must have ID!');
      return;
    }

    this.inputs = {
      letterInput: inputsWrapper.querySelector('input:first-child'),
      numberInput: inputsWrapper.querySelector('input:last-child')
    };

    if (!this.inputs.letterInput) {
      console.error('letterInput not found in wrapper');
      return;
    }
    if (!this.inputs.numberInput) {
      console.error('numberInput not found in wrapper');
      return;
    }

    // Initialize keydown and keyup events
    this._keyDownEvents();
    this._keyUpEvents();
  }

  /* PUBLIC FUNCTIONS */

  /**
   * Focus to number input
   *
   * @returns {undefined}
   */
  focusToNumbersInput() {
    this.inputs.numberInput.focus();
    this.inputs.numberInput.select();
  }

  /**
   * Focus to letters input
   *
   * @returns {undefined}
   */
  focusToLettersInput() {
    this.inputs.letterInput.focus();
    this.inputs.letterInput.select();
  }

  /**
   * Get inserted full license
   *
   * @returns {string} - License
   *
   * @example
   * const insertedLicense = window.licenseInputs['emission-calculator'].getLicense()
   *  -> insertedLicense output: 'ABC-123'
   */
  getLicense() {
    return `${this.getLicenseLetters()}-${this.getLicenseNumbers()}`;
  }

  /**
   * Get inserted license letters
   *
   * @returns {string}
   */
  getLicenseLetters() {
    return this.inputs.letterInput.value;
  }

  /**
   * Get inserted license numbers
   *
   * @returns {string}
   */
  getLicenseNumbers() {
    return this.inputs.numberInput.value;
  }

  /* PRIVATE FUNCTIONS */

  /**
   * Create keyup events to license letters and numbers inputs
   *
   * @returns {undefined}
   */
  _keyUpEvents() {
    const self = this;
    this.inputs.letterInput.addEventListener('keyup', keyUpEvent => {
      if(keyUpEvent.keyCode === 16) {
        return;
      }
      // If letter count is three, focus to license numbers input
      if(keyUpEvent.target.value.length === 3) {
        self.focusToNumbersInput();
      }
    });
  }

  /**
   * Create keydown events to license letters and numbers inputs
   *
   * @returns {undefined}
   */
  _keyDownEvents() {
    const self = this;
    this.inputs.letterInput.addEventListener('keydown', keyDownEvent => {
      // If user hits slash -key, move to numbers input
      if(keyDownEvent.keyCode === 189) {
        keyDownEvent.preventDefault();
        self.focusToNumbersInput();
      }

      // Prevent user to press tab-button before third letter
      // So prevent user to get annoyed
      if(keyDownEvent.keyCode === 9 && keyDownEvent.target.value.length === 3) {
        event.preventDefault();
      }
    })
  }
}

export default LicenseInput;
